import React from "react";

import type { LaneRendererProps } from "../lane.types";
import { GLASS_SECONDARY_TEXT_CLASS, compressStateChips, itemVisualDensity, overlapCounts, productTone, readDirectCouplingLabel, readNumberMeta, readStringMeta, visibleLaneItems } from "./rendering";

function stateChip(label: string) {
  return <span className="rounded-full border border-violet-300/18 bg-violet-300/12 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em] text-violet-50/86">{label}</span>;
}

export default function NpcLogicLaneRenderer({ lane, items, viewport, syncBadge }: LaneRendererProps): JSX.Element {
  const tone = productTone(lane.productType);
  const visibleItems = visibleLaneItems(items, viewport);
  const overlaps = overlapCounts(visibleItems);
  const laneMode = readStringMeta({ ...lane, metadata: lane.metadata } as never, "mode");
  const laneAnchoredRangeLabel = readStringMeta({ ...lane, metadata: lane.metadata } as never, "anchoredRangeLabel");
  const laneThresholdCount = readNumberMeta({ ...lane, metadata: lane.metadata } as never, "thresholdCount");
  const laneStateLabels = [
    laneMode,
    laneThresholdCount != null && laneThresholdCount > 0 ? `${laneThresholdCount} thresholds` : null,
    laneAnchoredRangeLabel,
  ].filter((value): value is string => Boolean(value));
  const directCouplingLabel = readDirectCouplingLabel(lane.metadata ?? null, lane.id, viewport.lodTier);
  const compressedLaneState = compressStateChips(laneStateLabels, viewport.lodTier);
  const showBodyReason = viewport.lodTier !== "far";

  return (
    <div className="relative h-full min-h-[48px]" data-lane-product="npc_logic" data-lane-id={lane.id}>
      <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-violet-200/10" />
      {visibleItems.length > 0 ? (
        visibleItems.map(({ item, leftPx, widthPx }) => {
          const density = itemVisualDensity(widthPx);
          const label = "label" in item
            ? item.label
            : item.itemType === "event"
              ? item.eventKey
              : item.itemType === "overlay"
                ? item.overlayKey
                : item.id;
          const status = readStringMeta(item, "status") ?? readStringMeta(item, "mode") ?? item.sourceType ?? null;
          const actor = readStringMeta(item, "actor") ?? readStringMeta(item, "npcId") ?? readStringMeta(item, "characterId");
          const strength = item.itemType === "event"
            ? item.strength ?? readNumberMeta(item, "strength")
            : readNumberMeta(item, "strength");
          const safety = readNumberMeta(item, "safety") ?? 0;
          const overlapCount = overlaps.get(item.id) ?? 1;
          const itemStateLabels = [
            actor,
            status,
            `safe ${Math.round(safety * 100)}%`,
            strength != null ? `${Math.round(strength * 100)}%` : null,
            overlapCount > 1 ? `x${overlapCount}` : null,
          ].filter((value): value is string => Boolean(value));
          const compressedItemState = compressStateChips(itemStateLabels, viewport.lodTier);
          const showNearDetail = viewport.lodTier === "near" && density === "regular";
          const showCompactDetail = viewport.lodTier === "mid" || density === "compact";

          return (
            <div
              key={item.id}
              data-visual-lane-item-id={item.id}
              className="absolute top-1/2 h-10 -translate-y-1/2 overflow-hidden rounded-xl border border-violet-300/24 bg-[linear-gradient(135deg,rgba(167,139,250,0.20),rgba(91,33,182,0.16),rgba(2,6,23,0.20))] px-3 shadow-[0_8px_20px_rgba(124,58,237,0.10)]"
              style={{ left: leftPx, width: widthPx }}
              title={label}
            >
              <div className="relative flex h-full items-center justify-between gap-2">
                <div className="min-w-0">
                  {showNearDetail ? (
                    <>
                      <div className="truncate text-[11px] font-semibold text-violet-50">{label}</div>
                      <div className="mt-0.5 flex items-center gap-1.5 overflow-hidden">
                        {actor ? stateChip(actor) : null}
                        {status ? stateChip(status) : null}
                        {stateChip(`safe ${Math.round(safety * 100)}%`)}
                      </div>
                    </>
                  ) : showCompactDetail ? (
                    <>
                      <div className="truncate text-[11px] font-semibold text-violet-50">{label}</div>
                      <div className="mt-0.5 flex items-center gap-1 overflow-hidden">
                        {compressedItemState.visible.map((state) => (
                          <React.Fragment key={state}>{stateChip(state)}</React.Fragment>
                        ))}
                        {compressedItemState.hiddenCount > 0 ? stateChip(`+${compressedItemState.hiddenCount} more`) : null}
                        {directCouplingLabel ? stateChip(directCouplingLabel) : null}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-1.5 text-[8px] font-semibold uppercase tracking-[0.14em] text-violet-50/82">
                      <span>NPC</span>
                      {strength != null ? <span>{Math.round(strength * 100)}%</span> : null}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {showNearDetail && strength != null ? stateChip(`${Math.round(strength * 100)}%`) : null}
                  {showNearDetail && overlapCount > 1 ? stateChip(`x${overlapCount}`) : null}
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className={["min-h-[56px] rounded-2xl border px-3 py-3", tone.emptyClassName].join(" ")}>
          <div className="flex items-center gap-2 text-[11px] font-semibold">
            <span>NPC logic projections are waiting on derived events.</span>
            {compressedLaneState.visible.map((state) => (
              <React.Fragment key={state}>{stateChip(state)}</React.Fragment>
            ))}
            {compressedLaneState.hiddenCount > 0 ? stateChip(`+${compressedLaneState.hiddenCount} more`) : null}
            {directCouplingLabel ? stateChip(directCouplingLabel) : null}
          </div>
          {showBodyReason ? (
          <div className={["mt-1 text-[10px]", GLASS_SECONDARY_TEXT_CLASS].join(" ")}>
            {syncBadge?.reason ?? (laneAnchoredRangeLabel
              ? `Read-only NPC timing or state events will bind to ${laneAnchoredRangeLabel} once lane state supplies them.`
              : "Read-only NPC timing or state events will render here once lane state supplies them.")}
          </div>
          ) : null}
        </div>
      )}
    </div>
  );
}